/**
 * Custom-column cell for `progress` columns. The value is a percentage
 * (`number`, 0–100); the cell renders a bar with the figure beside it and
 * click-to-edit like the number cell.
 *
 * Out-of-range input is clamped rather than rejected: the bar has no way to
 * draw 140%, and silently keeping a value the cell cannot show is worse than
 * pinning it to the end of the scale.
 */
import { Progress } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { useInlineCellEdit } from '../../lib/useInlineCellEdit';
import { EM_DASH } from './constants';

interface ProgressCustomCellProps {
  value: number | undefined;
  onChange: (next: number | null) => void;
  /** Shows the bar with nothing to click, for someone who cannot change it. */
  readOnly?: boolean;
}

const clampPercent = (n: number): number => Math.min(100, Math.max(0, Math.round(n)));

export function ProgressCustomCell({ value, onChange, readOnly }: ProgressCustomCellProps) {
  const { t } = useTranslation('trackers');

  const { editing, inputProps, triggerProps } = useInlineCellEdit(
    value !== undefined ? String(value) : '',
    (draft) => {
      if (draft === '') {
        if (value !== undefined) onChange(null);
        return;
      }
      const parsed = Number(draft);
      if (!Number.isFinite(parsed)) return;
      const next = clampPercent(parsed);
      if (next !== value) onChange(next);
    },
  );

  const display =
    value === undefined ? (
      <span className="w-full text-center text-muted-foreground">{EM_DASH}</span>
    ) : (
      <>
        <Progress
          value={value}
          className="h-1.5 flex-1"
          aria-label={t('trackersBoard.progressCustomCell.percentComplete', { value })}
        />
        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {t('trackersBoard.progressCustomCell.percent', { value })}
        </span>
      </>
    );

  if (readOnly) {
    return <div className="flex w-full items-center gap-2 px-1 text-sm">{display}</div>;
  }

  if (editing) {
    return (
      <input
        {...inputProps}
        type="number"
        inputMode="numeric"
        min={0}
        max={100}
        className="w-full bg-transparent text-center text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    );
  }

  return (
    <button
      {...triggerProps}
      className="flex w-full items-center gap-2 rounded px-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {display}
    </button>
  );
}
